import {
  type Condition,
  type FindingExclusionFilterConditions,
  ConditionOperatorEnum,
  DetectorType,
  FindingExclusionDetectorType,
  FindingExclusionScope,
  FindingObjectType,
  type ResourceReference,
} from '@repo/api-gw-sdk';

import { collectValueConditions } from '@/types/advanceFilter';

export const EXCLUSION_SCOPE_GLOBAL = 'Global';
export const EXCLUSION_SCOPE_RESOURCE = 'Resource';

export const RESOURCE_FILTER_PROPERTY = 'providerResourceId';

const FINDING_EXCLUSION_SCOPES: Partial<Record<string, FindingExclusionScope>> =
  {
    [EXCLUSION_SCOPE_GLOBAL]:
      FindingExclusionScope.FINDING_EXCLUSION_SCOPE_ACCOUNT,
    [EXCLUSION_SCOPE_RESOURCE]:
      FindingExclusionScope.FINDING_EXCLUSION_SCOPE_RESOURCE,
  };

const toFindingExclusionScopes = (values: string[]) =>
  values.flatMap((v) => FINDING_EXCLUSION_SCOPES[v] ?? []);

const FINDING_EXCLUSION_DETECTORS: Partial<
  Record<string, FindingExclusionDetectorType>
> = {
  [DetectorType.RansomwareBehavior]:
    FindingExclusionDetectorType.FINDING_EXCLUSION_DETECTOR_TYPE_RANSOMWARE_BEHAVIOR,
  [DetectorType.DataAnomaly]:
    FindingExclusionDetectorType.FINDING_EXCLUSION_DETECTOR_TYPE_DATA_ANOMALY,
  [DetectorType.Malware]:
    FindingExclusionDetectorType.FINDING_EXCLUSION_DETECTOR_TYPE_MALWARE,
};

const FINDING_OBJECT_TYPES: string[] = Object.values(FindingObjectType);

const isFindingObjectType = (value: string): value is FindingObjectType =>
  FINDING_OBJECT_TYPES.includes(value);

// Undefined for a detector no exclusion can suppress, such as the resource-level AI data risk.
export const toFindingExclusionDetector = (
  detector: string
): FindingExclusionDetectorType | undefined =>
  FINDING_EXCLUSION_DETECTORS[detector];

const toFindingExclusionDetectors = (values: string[]) =>
  values.flatMap((v) => toFindingExclusionDetector(v) ?? []);

/**
 * Indexes the exclusion resources by provider resource ID. A provider ID shared by more than one resource, e.g. the same
 * name in two cloud accounts, maps to undefined: it can't be attributed to either of them.
 */
export const indexResourcesByProviderId = (
  resources: ResourceReference[]
): Map<string, ResourceReference | undefined> => {
  const byProviderId = new Map<string, ResourceReference | undefined>();
  for (const resource of resources) {
    byProviderId.set(
      resource.providerResourceId,
      byProviderId.has(resource.providerResourceId) ? undefined : resource
    );
  }
  return byProviderId;
};

/**
 * Maps the exclusion table's filter conditions to the list API's filters. Values the API doesn't know are dropped,
 * since it rejects them. Returns null when a scope, type or detector filter holds only such values: no exclusion can
 * match it, and sending it without them would widen it to every exclusion.
 */
export const toFindingExclusionFilters = (
  condition: Condition | undefined
): FindingExclusionFilterConditions | null => {
  const filters: FindingExclusionFilterConditions = {};

  for (const { property, operator, value = [] } of condition
    ? collectValueConditions(condition)
    : []) {
    const isNegated = operator === ConditionOperatorEnum.NotIn;
    switch (property) {
      case 'scope': {
        const scopes = toFindingExclusionScopes(value);
        if (!isNegated && value.length && !scopes.length) {
          return null;
        }
        filters.scope = isNegated ? { notIn: scopes } : { _in: scopes };
        break;
      }
      case RESOURCE_FILTER_PROPERTY:
        // The API has no negated resource filter, so the Resource filter offers only "is one of".
        if (!isNegated && value.length) {
          filters.resource = { _in: value };
        }
        break;
      case 'type': {
        const types = value.filter(isFindingObjectType);
        if (!isNegated && value.length && !types.length) {
          return null;
        }
        filters.type = isNegated ? { notIn: types } : { _in: types };
        break;
      }
      case 'detector': {
        const detectors = toFindingExclusionDetectors(value);
        if (!isNegated && value.length && !detectors.length) {
          return null;
        }
        filters.detector = isNegated
          ? { notIn: detectors }
          : { _in: detectors };
        break;
      }
      case 'path':
        if (operator === ConditionOperatorEnum.Contains && value.length) {
          filters.value = { contains: value };
        }
        break;
    }
  }
  return filters;
};
